<template>
  <div class="asc-visualize-fault-code-graph">
    <template v-if="!isEmpty">
      <asc-stock-chart :options="getChartOptions"></asc-stock-chart>
    </template>
    <asc-no-data v-if="isEmpty"></asc-no-data>
    <asc-filter-legend
      class="asc-visualize-fault-code-graph--legend"
      :items="getLegendItems"
      :on-item-click="handleLegendItemClick"
      :items-selected="legendItemsSelected"
    ></asc-filter-legend>
  </div>
</template>

<script>
import AscNoData from '@/components/atoms/AscNoData'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import AscStockChart from '@/components/molecules/AscStockChart'
import variables from '@/styles/_varsJs.scss'
import { getFormattedUTCDate } from '@/utilities/dates'
import _ from 'lodash'

export default {
  name: 'AscVisualizeFaultCodeGraph',

  components: {
    AscStockChart,
    AscNoData,
    AscFilterLegend,
  },

  props: {
    data: {
      type: Array,
      default: () => [],
    },
    tailNumber: {
      type: String,
      default: '',
    },
    messageCode: {
      type: String,
      default: '',
    },
  },

  data() {
    const _that = this
    return {
      legendItemsSelected: [],
      defaultOptions: {
        chart: {
          type: 'line',
          alignTicks: false,
          height: 270,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            showInNavigator: false,
            gapSize: 1,
            states: {
              inactive: {
                opacity: 1,
              },
              hover: {
                enabled: false,
              },
            },
          },
          line: {
            marker: {
              enabled: true,
              radius: 3,
              symbol: 'circle',
            },
          },
        },
        rangeSelector: {
          enabled: false,
        },
        time: {
          useUTC: true,
        },
        title: {
          text: '',
        },
        tooltip: {
          split: false,
          shared: true,
          useHTML: true,
          formatter: function () {
            const tooltip = this.points.map(point => {
              const formattedDate = getFormattedUTCDate(point.x)
              const seriesName = point.series.name
              if (seriesName === 'Fleet Count') {
                return `${_that.messageCode} occurred ${point.y} times <br/> on your fleet on ${formattedDate}`
              }
              return `${_that.messageCode} occurred ${point.y} times <br/> for ${_that.tailNumber} on ${formattedDate}<br/><br/>`
            })

            return tooltip
          },
          backgroundColor: variables.gray900,
          borderWidth: 0,
          borderRadius: 5,
          style: {
            color: variables.primarywhite,
            fontSize: variables.fontsizesm,
            whiteSpace: 'break-spaces',
            textAlign: 'center',
          },
        },
      },
    }
  },

  computed: {
    isEmpty() {
      return this.data.length === 0 || this.legendItemsSelected.length === 2
    },

    getLegendItems() {
      return [
        {
          header: '',
          items: [
            {
              text: this.tailNumber,
              styleColor: '#007A75',
              borderRadius: '50%',
            },
            {
              text: 'FULL FLEET',
              styleColor: '#B9B9B9',
              borderRadius: '50%',
            },
          ],
        },
      ]
    },

    getChartOptions() {
      const xAxisOptions = this.buildXAxisOptions
      const yAxisOptions = this.buildYAxisOptions
      const navigator = this.buildNavigatorOptions
      const series = this.buildSeries

      return {
        ..._.merge(this.defaultOptions),
        ...series,
        ...xAxisOptions,
        ...yAxisOptions,
        ...navigator,
      }
    },

    buildXAxisOptions() {
      return {
        xAxis: {
          type: 'datetime',
          crosshair: false,
          gridLineWidth: 1,
          title: {
            text: 'Time',
          },
        },
      }
    },

    buildYAxisOptions() {
      const _that = this
      const series = this.buildSeries

      return {
        yAxis: [
          {
            title: {
              text: _that.tailNumber
                ? `${_that.tailNumber} Count`
                : 'Tail Count',
            },
            opposite: false,
            lineWidth: 1,
            gridLineWidth: 0,
            visible: series.series[0].visible,
          },
          {
            title: {
              text: 'Fleet Count',
            },
            opposite: false,
            gridLineWidth: 1,
            visible: series.series[1].visible,
          },
        ],
      }
    },

    buildNavigatorOptions() {
      return {
        navigator: {
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
      }
    },

    buildSeries() {
      return {
        series: [
          {
            name: 'Tail Count',
            data: this.data?.map(item => {
              return [new Date(item.timeStamp).getTime(), item.tailCount]
            }),
            color: '#007A75',
            visible: !this.legendItemsSelected.includes(this.tailNumber),
          },
          {
            name: 'Fleet Count',
            data: this.data?.map(item => {
              return [new Date(item.timeStamp).getTime(), item.fleetCount]
            }),
            color: '#B9B9B9',
            yAxis: 1,
            visible: !this.legendItemsSelected.includes('FULL FLEET'),
          },
        ],
      }
    },
  },

  watch: {
    data: {
      handler() {
        this.legendItemsSelected = []
      },
    },
  },

  methods: {
    handleLegendItemClick(data) {
      const isFound = this.legendItemsSelected.includes(data)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== data,
        )
      } else {
        this.legendItemsSelected.push(data)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-visualize-fault-code-graph {
  padding: 15px 0;

  &--legend {
    .asc-legend-list > ul {
      display: flex;

      .asc-legend-list-item {
        width: 30%;
      }
    }
  }

  .highcharts-reset-zoom {
    opacity: 0.4;
  }

  .highcharts-reset-zoom:hover {
    opacity: 1;
  }
}

.reset-zoom-btn {
  position: absolute;
  right: 50px;
  top: 5px;
}
</style>
